import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "react-query";
import { toast } from "react-toastify";
import { getProfile, updateProfile, changePassword, grantCaAccess, getCaList, revokeCaAccess } from "../Apis/apis";

const Profile = () => {
  const [profileData, setProfileData] = useState({ name: "", email: "", phone: "" });
  const [passwordData, setPasswordData] = useState({ oldPassword: "", password: "", confirmPassword: "" });
  const [caEmail, setCaEmail] = useState("");

  const { data: caListData, refetch: refetchCaList } = useQuery("caList", async () => {
    const response = await getCaList();
    return response.data?.payload?.data || [];
  });

  const { isLoading, refetch } = useQuery(
    ["profileData"],
    async () => {
      const response = await getProfile();
      return response.data?.payload || {};
    },
    {
      onSuccess: (data) => {
        setProfileData({
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
        });
      },
    }
  );

  const updateProfileMutation = useMutation(
    async (data) => {
      const res = await updateProfile(data);
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success("Profile updated successfully!");
        refetch();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || "Failed to update profile");
      },
    }
  );

  const changePasswordMutation = useMutation(
    async (data) => {
      const res = await changePassword({ oldPassword: data.oldPassword, password: data.password, confirmPassword: data.confirmPassword });
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success("Password changed successfully! Please login again.");
        setTimeout(() => {
          localStorage.removeItem("token");
          window.location.href = "/login";
        }, 2000);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || "Failed to change password");
      },
    }
  );

  const grantCaAccessMutation = useMutation(
    async (email) => {
      const res = await grantCaAccess({ caEmail: email });
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success("CA Access granted successfully!");
        setCaEmail("");
        refetchCaList();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || "Failed to grant CA access");
      },
    }
  );

  const revokeCaAccessMutation = useMutation(
    async (caId) => {
      const res = await revokeCaAccess(caId);
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success("CA Access revoked successfully!");
        refetchCaList();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || "Failed to revoke CA access");
      },
    }
  );

  const handleGrantAccess = (e) => {
    e.preventDefault();
    if (!caEmail) return;
    grantCaAccessMutation.mutate(caEmail);
  };

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    updateProfileMutation.mutate(profileData);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordData.password !== passwordData.confirmPassword) {
      toast.error("New passwords do not match!");
      return;
    }
    if (passwordData.password.length < 6) {
      toast.error("Password must be at least 6 characters!");
      return;
    }
    changePasswordMutation.mutate(passwordData);
  };

  if (isLoading) {
    return (
      <div className="container" style={{ paddingBottom: "40px" }}>
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingBottom: "40px", paddingTop: "24px" }}>
      <div className="mb-4">
        <h4 style={{ fontWeight: "800", margin: "0", letterSpacing: "0.02em" }}>My Profile</h4>
        <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginTop: "4px" }}>Manage your personal information and security settings</p>
      </div>

      <div className="row g-4">
        {/* Profile Information */}
        <div className="col-12 col-lg-6">
          <div className="glass-card h-100" style={{ padding: "24px" }}>
            <div className="d-flex align-items-center gap-3 mb-4">
              <div style={{
                width: "48px", height: "48px", borderRadius: "12px",
                background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontSize: "1.2rem", fontWeight: "800"
              }}>
                {profileData.name ? profileData.name.charAt(0).toUpperCase() : "U"}
              </div>
              <div>
                <h6 style={{ fontWeight: "700", margin: 0, fontSize: "1.1rem" }}>Personal Information</h6>
                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Update your name, email, and phone number</div>
              </div>
            </div>

            <form onSubmit={handleProfileSubmit}>
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "0.85rem", fontWeight: "600" }}>Full Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "0.85rem", fontWeight: "600" }}>Email Address</label>
                <input
                  type="email"
                  className="form-control"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="form-label" style={{ fontSize: "0.85rem", fontWeight: "600" }}>Phone Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary w-100"
                style={{ fontWeight: "700", borderRadius: "10px", padding: "10px" }}
                disabled={updateProfileMutation.isLoading}
              >
                {updateProfileMutation.isLoading ? "Saving..." : "Save Changes"}
              </button>
            </form>
          </div>
        </div>

        {/* Security Settings */}
        <div className="col-12 col-lg-6">
          <div className="glass-card h-100" style={{ padding: "24px" }}>
            <div className="d-flex align-items-center gap-3 mb-4">
              <div style={{
                width: "48px", height: "48px", borderRadius: "12px",
                background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--danger)", fontSize: "1.2rem", fontWeight: "800"
              }}>
                🔒
              </div>
              <div>
                <h6 style={{ fontWeight: "700", margin: 0, fontSize: "1.1rem" }}>Security</h6>
                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Ensure your account is using a long, random password</div>
              </div>
            </div>

            <form onSubmit={handlePasswordSubmit}>
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "0.85rem", fontWeight: "600" }}>Current Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={passwordData.oldPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "0.85rem", fontWeight: "600" }}>New Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={passwordData.password}
                  onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="form-label" style={{ fontSize: "0.85rem", fontWeight: "600" }}>Confirm New Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-outline-danger w-100"
                style={{ fontWeight: "700", borderRadius: "10px", padding: "10px" }}
                disabled={changePasswordMutation.isLoading}
              >
                {changePasswordMutation.isLoading ? "Updating..." : "Update Password"}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="row g-4 mt-1">
        {/* CA Access Management */}
        <div className="col-12">
          <div className="glass-card" style={{ padding: "24px" }}>
            <div className="d-flex align-items-center gap-3 mb-4">
              <div style={{
                width: "48px", height: "48px", borderRadius: "12px",
                background: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#10b981", fontSize: "1.2rem", fontWeight: "800"
              }}>
                👥
              </div>
              <div>
                <h6 style={{ fontWeight: "700", margin: 0, fontSize: "1.1rem" }}>Chartered Accountant Access</h6>
                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Share read-only access to your account with your CA</div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-4 mb-md-0">
                <h6 style={{ fontWeight: "600", fontSize: "0.95rem", marginBottom: "16px" }}>Grant New Access</h6>
                <form onSubmit={handleGrantAccess} className="d-flex gap-2">
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Enter CA's Email Address"
                    value={caEmail}
                    onChange={(e) => setCaEmail(e.target.value)}
                    required
                  />
                  <button
                    type="submit"
                    className="btn btn-success"
                    style={{ fontWeight: "600", borderRadius: "10px", padding: "8px 20px", whiteSpace: "nowrap" }}
                    disabled={grantCaAccessMutation.isLoading}
                  >
                    {grantCaAccessMutation.isLoading ? "Sending..." : "Grant Access"}
                  </button>
                </form>
              </div>
              
              <div className="col-md-6">
                <h6 style={{ fontWeight: "600", fontSize: "0.95rem", marginBottom: "16px" }}>People with Access</h6>
                {caListData && caListData.length > 0 ? (
                  <div className="list-group">
                    {caListData.map((ca, idx) => (
                      <div key={idx} className="list-group-item d-flex justify-content-between align-items-center" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(0,0,0,0.05)", borderRadius: "8px", marginBottom: "8px" }}>
                        <div>
                          <div style={{ fontWeight: "600", fontSize: "0.9rem" }}>{ca.name}</div>
                          <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{ca.email}</div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge bg-success" style={{ opacity: 0.8 }}>Active</span>
                          <button 
                            className="btn btn-sm btn-outline-danger" 
                            style={{ fontSize: "0.75rem", padding: "2px 6px" }}
                            onClick={() => revokeCaAccessMutation.mutate(ca.userId)}
                            disabled={revokeCaAccessMutation.isLoading}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: "20px", textAlign: "center", background: "rgba(0,0,0,0.02)", borderRadius: "8px", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                    No one has been granted access yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
